import { useMemo } from "react"
import moment from "moment"
import { useSelector } from "react-redux"
import { formatEventLabels } from "pagesV2/pro/Calendar/utils"
import { UNASSIGNED_ID } from "../constants"

const buildEquipmentDayMap = (eq, calendarDays, events) => {
  const dayMap = {}
  calendarDays.forEach((day) => {
    const dayStr = day.format("YYYY-MM-DD")
    dayMap[dayStr] = events
      .filter((ev) =>
        moment(ev.date).isSame(day, "day") &&
        (ev.assigned_equipment || []).some((item) => item.name === eq.name || item.boat_id === eq.id)
      )
      .map(formatEventLabels)
  })
  return dayMap
}

const useEquipmentGridData = (calendarDays, events, equipmentIds, categoryIds) => {
  const allEquipment = useSelector((state) =>
    (state.calendar?.calendarEquipment || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }))
  )

  const equipmentList = useMemo(() => {
    const filteredIds = equipmentIds?.filter((id) => id !== UNASSIGNED_ID)
    const onlyUnassigned = equipmentIds?.includes(UNASSIGNED_ID) && filteredIds?.length === 0
    if (onlyUnassigned) return []

    const assignedIds = new Set()
    if (categoryIds?.length > 0) {
      events.forEach((ev) => {
        (ev.assigned_equipment || []).forEach((item) => {
          if (item.boat_id) assignedIds.add(item.boat_id)
          if (item.id) assignedIds.add(item.id)
        })
      })
    }

    if (filteredIds?.length > 0 && categoryIds?.length > 0) {
      const merged = new Set([...filteredIds, ...assignedIds])
      return allEquipment.filter((eq) => merged.has(eq.id))
    }
    if (filteredIds?.length > 0) return allEquipment.filter((eq) => filteredIds.includes(eq.id))
    if (categoryIds?.length > 0) return allEquipment.filter((eq) => assignedIds.has(eq.id))
    return allEquipment
  }, [allEquipment, equipmentIds, categoryIds, events])

  const hasEquipmentFilters = equipmentIds?.filter((id) => id !== UNASSIGNED_ID)?.length > 0
  const hasCategoryFilters = categoryIds?.length > 0
  const hasAnyFilters = hasEquipmentFilters || hasCategoryFilters
  const showUnassigned = equipmentIds?.includes(UNASSIGNED_ID) || !hasAnyFilters

  const unassignedEventsByDay = useMemo(() => {
    const dayMap = {}
    calendarDays.forEach((day) => {
      const dayStr = day.format("YYYY-MM-DD")
      dayMap[dayStr] = events
        .filter((ev) => moment(ev.date).isSame(day, "day") && (!ev.assigned_equipment || ev.assigned_equipment.length === 0))
        .map(formatEventLabels)
    })
    return dayMap
  }, [calendarDays, events])

  const eventsByEquipmentAndDay = useMemo(() => {
    const map = {}
    equipmentList.forEach((eq) => { map[eq.name] = buildEquipmentDayMap(eq, calendarDays, events) })
    return map
  }, [equipmentList, calendarDays, events])

  return { equipmentList, showUnassigned, unassignedEventsByDay, eventsByEquipmentAndDay }
}

export default useEquipmentGridData
